import {
  Flex,
  Box,
  Text,
  useBreakpointValue,
  Center,
  Divider,
} from "@chakra-ui/react";
import { GCChannel } from "../../../types";
import { StreamTabs } from "../../../components/tabs/StreamTabs";
import { CollapsibleNavBar } from "../../../components/navbar/CollapsibleNavBar";
import { useIsSmallBreakpoint } from "../../../utils/useBreakpoints";
import { useTopBarHeight } from "../../../hooks/userTopBarHeight";

interface HeaderNavigationBarProps {
  showChannelName: boolean;
  activeChannel: GCChannel;
  activeStreamId?: string;
  isCollapsed?: boolean;
}

export function HeaderNavigationBar({
  showChannelName,
  activeChannel,
  activeStreamId,
  isCollapsed,
}: HeaderNavigationBarProps) {
  /// Determine if the navbar should wrap to a column layout on smaller screens, and if the navbar should
  /// be collapsed.
  const isSmallScreen = useIsSmallBreakpoint();
  const topBarHeight = useTopBarHeight();

  const showStreams = activeChannel.streams.length > 1;

  return (
    <CollapsibleNavBar
      shouldCollapse={isSmallScreen}
      isCollapsed={isCollapsed}
      isWrapped={showChannelName && showStreams}
      boxProps={{
        width: "100%",
        zIndex: 1,
        position: "sticky",
        top: `${topBarHeight.get()}px`,
        backgroundColor: "charcoal.charcoal",
        transform: isSmallScreen
          ? !isCollapsed
            ? "translateY(0)"
            : `translateY(-${topBarHeight.get()}px)`
          : undefined,
        display: "flex",
      }}
      nonCollapsibleChildren={
        showStreams ? (
          <StreamTabHeader
            activeChannel={activeChannel}
            activeStreamId={activeStreamId}
            showChannelName={showChannelName}
            showStreams={showStreams}
          />
        ) : null
      }
    >
      {showStreams ? null : (
        <StreamTabHeader
          activeChannel={activeChannel}
          activeStreamId={activeStreamId}
          showChannelName={showChannelName}
          showStreams={showStreams}
        />
      )}
    </CollapsibleNavBar>
  );
}

export function StreamTabHeader({
  activeChannel,
  activeStreamId,
  showChannelName = false,
  showStreams = false,
}: {
  activeChannel: GCChannel;
  activeStreamId?: string;
  showChannelName?: boolean;
  showStreams?: boolean;
}) {
  const shouldWrap = useBreakpointValue({ base: true, md: false });
  return (
    <Flex width="100%" pt={{ base: 4, lg: 8 }} pb={4}>
      {showChannelName && (
        <Box bgColor="charcoal.charcoal" pl={{ base: 4, lg: 0 }}>
          <Flex
            direction={!shouldWrap ? "row" : "column"}
            alignItems={!shouldWrap ? "center" : "flex-start"}
          >
            <Flex
              flexDirection="row"
              alignItems="center"
              width="auto"
              flexShrink={0}
              flexWrap="nowrap"
              gap={{ base: 3, md: 4 }}
            >
              <img
                src={activeChannel.config[0].icon} // Always retrieving dark mode icon
                alt="channel icon"
                style={{ width: "38px", height: "auto" }}
              />

              <Text
                variant="navbar"
                color="dune.dune"
                fontWeight={700}
                fontSize={{ base: "18px", md: "24px" }}
                align="center"
                whiteSpace="nowrap"
              >
                {activeChannel.display_name}
              </Text>
              {!shouldWrap && (
                <Center height="40px">
                  <Divider
                    height="40px"
                    orientation="vertical"
                    pl={2}
                    visibility={
                      activeChannel.streams.length > 1 ? "visible" : "hidden"
                    }
                  />
                </Center>
              )}
            </Flex>
          </Flex>
        </Box>
      )}
      {showStreams && (
        <Box
          flex="1"
          overflowX="auto"
          bgColor="charcoal.charcoal"
          pl={{ base: 3, lg: 0 }}
        >
          <StreamTabs
            streams={activeChannel.streams}
            activeStreamId={activeStreamId}
          />
        </Box>
      )}
    </Flex>
  );
}
